import {
  getAuthenticationData,
  refreshAccessToken,
} from "@mirage/service-auth";
import type { LogoutReason } from "@mirage/service-auth/types";
import { tagged } from "@mirage/service-logging";

const logger = tagged("auth-error-handler");

export const LOGOUT_ERROR_MAP: Record<number, Set<string>> = {
  400: new Set(["invalid_grant"]),
  401: new Set(["user_suspended", "missing_scope"]),
};

export const REFRESH_TOKEN_ERROR_MAP: Record<number, Set<string>> = {
  401: new Set(["invalid_access_token", "expired_access_token"]),
};

export const isLogoutRequiredError = (
  error: string,
): error is "invalid_grant" | "user_suspended" | "missing_scope" =>
  LOGOUT_ERROR_MAP[400].has(error) || LOGOUT_ERROR_MAP[401].has(error);

export const isRefreshTokenRequiredError = (
  error: string,
): error is "invalid_access_token" => REFRESH_TOKEN_ERROR_MAP[401].has(error);

let authErrorCheckInProgress = false;

export const handleAuthErrorIfNeeded = async (
  errCode: number,
  errTag: string,
  accessToken: string | undefined,
  onLogout: (reason: LogoutReason) => Promise<void>,
): Promise<void> => {
  if (authErrorCheckInProgress) return;
  authErrorCheckInProgress = true;

  try {
    const authData = await getAuthenticationData();
    if (!authData?.accessToken) {
      logger.log("No action needed: user is already signed out");
    } else if (authData?.accessToken && authData.accessToken !== accessToken) {
      logger.log("No action needed: user has a new access token");
    } else if (
      isLogoutRequiredError(errTag) ||
      isRefreshTokenRequiredError(errTag)
    ) {
      let failedToRefreshToken = false;
      if (
        authData?.refreshToken &&
        REFRESH_TOKEN_ERROR_MAP[errCode]?.has(errTag)
      ) {
        logger.log("Attempting to refresh access token");
        try {
          failedToRefreshToken = !(await refreshAccessToken());
        } catch (e) {
          logger.error("Failed to refresh access token", e);
          failedToRefreshToken = true;
        }
      }
      if (LOGOUT_ERROR_MAP[errCode]?.has(errTag) || failedToRefreshToken) {
        logger.warn(`Auto-signout: authError=${errTag}`);
        await onLogout(`auto--${errTag}`);
      }
    }
  } catch (e) {
    logger.error("Failed to handle auth error", e);
  } finally {
    authErrorCheckInProgress = false;
  }
};
